import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
	ActionIcon,
	Alert,
	Button,
	Checkbox,
	Flex,
	Group,
	Modal,
	SegmentedControl,
	Badge,
	Stack,
	Text,
} from '@mantine/core';
import {
	EMPTY_FLOW_RULE_SCENARIO,
	flowFieldNames,
	flowRuleUsage,
	parseDocumentFlow,
} from '@dealstack/shared/lib/common/business/documentFlow';
import type {
	DocumentFlowDoc,
	FlowRules,
	FlowRuleScenario,
} from '@dealstack/shared/lib/common/business/documentFlow';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { FlowDocumentEditorPane } from '@dealstack/frontend/shared/components/DocumentEditor/FlowDocumentEditorPane';
import { RuleModal } from '@dealstack/frontend/shared/components/DocumentEditor/RuleModal';
import { RulesPanel } from '@dealstack/frontend/shared/components/DocumentEditor/RulesPanel';
import { RulePreviewPanel } from '@dealstack/frontend/shared/components/DocumentEditor/RulePreviewPanel';
import {
	isEmptyScenario,
	scenarioForRules,
} from '@dealstack/frontend/shared/components/DocumentEditor/rulePreview.utils';
import { ruleLabels } from '@dealstack/frontend/shared/components/DocumentEditor/rules.utils';
import type { NewRule } from '@dealstack/frontend/shared/components/DocumentEditor/rules.types';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	EditorMountSlot,
	EditorPaneSplit,
	EditorSidePanel,
	PaletteScroll,
	PaletteTab,
	PaletteTabs,
	PaletteTabsList,
} from '@dealstack/frontend/shared/components/DocumentEditor/flowDocumentEditor.styled';
import type { FlowDocumentEditorHandle } from '@dealstack/frontend/shared/components/DocumentEditor/flowDocumentEditor.types';
import {
	offeredFields,
	type OfferedFields,
} from '@dealstack/frontend/shared/components/DocumentEditor/dataPointFields';
import { isDataPointCollected } from '@dealstack/shared/lib/documents/programmeDataRequirements';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { getApiErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	isFirstHoldingPoint,
	SAMPLE_INVESTOR_ID,
} from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { SecurityScopeCard } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/SecurityScopeCard.tsx';
import {
	scopedFieldLabels,
	scopedPaletteFields,
	spansIssuers,
} from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/scopedBinding.utils.ts';
import type { TransactionScope } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { ToggleRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/FieldBindingEditor.styled.ts';
import { PreviewAsSelect } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/PreviewAsSelect.tsx';
import { usePreviewInvestorShortcuts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/usePreviewInvestorShortcuts.ts';
import { DocxFlowPreview } from './DocxFlowPreview.tsx';
import { mergeScopedFields } from './docxFlowEditor.utils.ts';
import type { EditTab, ViewMode } from './docxFlowEditor.types.ts';
import type { DocxFlowEditorAdapter } from './docxFlowEditor.adapter.ts';

interface Props {
	templateId: string;
	/**
	 * Everything that depends on who owns this template — see {@link DocxFlowEditorAdapter}.
	 * Must be a stable module-level constant: its members are hooks.
	 */
	adapter: DocxFlowEditorAdapter;
	/** Pinned at the top of the side panel, above the tabs — the document's own name. */
	panelHeader?: ReactNode;
	/** Folds an unsaved {@link panelHeader} edit into this editor's Save, so the one Save
	 *  button on screen writes it too. */
	panelHeaderEdit?: PanelHeaderEdit;
	onClose?: () => void;
}

/**
 * The Word master document, editable, with a preview of what generation will produce.
 *
 * Replaces the read-only canvas for a .docx: that rendered a PDF, so there was nothing to type
 * into and nothing to drop onto. Here the document IS the surface — type `{{` where a value
 * belongs, or drag a data point in from the list beside it — and the Preview toggle renders the
 * same document through the same renderer generation uses, with a recipient's real values in.
 *
 * A data point dragged in arrives already LINKED. The palette knows the Liquid path, so there
 * is no reason to make the author place a field and then bind it in a second screen; the
 * binding rides along with the save and is merged, never replaced.
 */

/**
 * Narrows a tab value from the control, refusing `signatures` where the document has no signing
 * configuration — the tab is not rendered in that case, so only a stale value can name it.
 */
const toEditTab = (value: string | null, isSignable: boolean): EditTab => {
	if (value === 'rules') {
		return 'rules';
	}
	return value === 'signatures' && isSignable ? 'signatures' : 'fields';
};

export const DocxFlowEditor = ({
	templateId,
	adapter,
	panelHeader,
	panelHeaderEdit,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { data, isLoading, isError } = adapter.useFlowDocument(templateId);
	const save = adapter.useSaveFlowDocument();

	const [viewMode, setViewMode] = useState<ViewMode>('edit');
	const [editTab, setEditTab] = useState<EditTab>('fields');
	const [previewInvestorId, setPreviewInvestorId] = useState<string | null>(SAMPLE_INVESTOR_ID);
	// On by default: an author opens the preview to check where their values land, and a
	// highlight nobody switches on shows them nothing. Session-local — it is how the author is
	// looking at the document, not something about the document.
	const [highlightFields, setHighlightFields] = useState(true);
	// Hand-set answers for what the rules read. Kept across recipients, so one "what if" can be
	// checked against several; never saved.
	const [scenario, setScenario] = useState<FlowRuleScenario>(EMPTY_FLOW_RULE_SCENARIO);

	const { data: catalogueData, isPending: isLoadingCatalogue } = adapter.useCatalogue(
		templateId,
		previewInvestorId
	);
	const catalogue = useMemo(() => catalogueData?.dataPoints ?? [], [catalogueData]);
	const securities = useMemo(() => catalogueData?.securities ?? [], [catalogueData]);
	const scopedSamples = useMemo(() => catalogueData?.scopedSamples ?? {}, [catalogueData]);
	const uncollectedRequirements = useMemo(
		() => catalogueData?.uncollectedRequirements ?? [],
		[catalogueData]
	);

	// Which security's fields the palette pins, if any — not part of the document, so it is
	// never saved on its own; it only decides what the palette OFFERS. The binding a dragged
	// scoped field carries is what makes the pin durable.
	const [scopedSecurityId, setScopedSecurityId] = useState('');
	// Qualifies the in-progress half of a scoped field's label. Its own key rather than reusing
	// the radio's "In progress": that one is a control's caption and is capitalised as one.
	const inProgressLabel = tMipManage(
		'master_documents.field_binding.security.scope.in_progress_qualifier',
		{ defaultValue: 'in progress' }
	);
	const [transactionScope, setTransactionScope] = useState<TransactionScope>('completed');
	const pinnedSecurity = securities.find((security) => security.id === scopedSecurityId);

	const [edited, setEdited] = useState<DocumentFlowDoc | null>(null);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	/**
	 * The rules, held beside the document rather than inside `edited`.
	 *
	 * `null` means "exactly as stored", which is what tells Save whether the rules are part of
	 * what is unsaved — the editor only reports CONTENT changes, so adding a rule without typing
	 * anything would otherwise look like no edit at all.
	 */
	const [editedRules, setEditedRules] = useState<FlowRules | null>(null);
	const [ruleModalOpen, setRuleModalOpen] = useState(false);
	/**
	 * Bumped on every new-rule open, so the modal remounts empty rather than keeping the last
	 * rule's test and name. And whether that new rule marks the selection: from the toolbar it
	 * does; from the Rules tab the caret may be anywhere, so the rule is created unmarked.
	 */
	const [newRuleOpening, setNewRuleOpening] = useState({ count: 0, marksSelection: true });
	/** The rule the modal is editing, or null when it is writing a new one. */
	const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
	/** The rule whose deletion is waiting to be confirmed. */
	const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
	// Per session, not persisted: the problems come off the stored conversion and are re-read
	// every time the editor opens, so a dismissal that outlived it would hide a real finding.
	const [problemsDismissed, setProblemsDismissed] = useState(false);
	// Set when the editor holds something the model cannot express — a pasted image with no
	// stated size, say. Saving is blocked while it is: what `edited` still holds is the document
	// from BEFORE that paste, and writing it would throw away everything since.
	const [cannotRepresent, setCannotRepresent] = useState(false);
	const editorRef = useRef<FlowDocumentEditorHandle>(null);
	// Latch for the loading gate below — see the comment there.
	const hasBuiltEditor = useRef(false);

	const {
		isAvailable: hasInvestorPreview,
		options: investorOptions,
		onDropdownOpen: onInvestorDropdownOpen,
		isFetching: isFetchingInvestors,
	} = adapter.useInvestorPreview({ preload: viewMode === 'preview' });

	usePreviewInvestorShortcuts({
		enabled: hasInvestorPreview && viewMode === 'preview' && !save.isPending,
		options: investorOptions,
		value: previewInvestorId,
		onChange: setPreviewInvestorId,
	});

	// The Liquid expression behind each name the palette has offered, so a field dragged in
	// arrives already linked. A ref, not state: nothing renders from it. Only the entries whose
	// field is actually IN the document are sent — see `bindingsFor` below.
	//
	// It ACCUMULATES, never resets. A field is placed from whatever the palette offered at the
	// time, and the scope card can move afterwards — a `unitsHeld` field placed under "completed"
	// leaves the palette the moment the radio flips to "in progress", and replacing the map
	// dropped its name, so the save sent no binding for it and the field was stored unlinked.
	// Safe because a name maps to exactly one expression for the document's lifetime: the
	// security and the source are both folded into it.
	const bindingByOfferedName = useRef<Record<string, string>>({});

	// Validated at the boundary: the endpoint types the document as an opaque object and the
	// editor needs the real shape. A model this cannot read is one no editor could open.
	const loaded = useMemo<DocumentFlowDoc | null>(() => {
		if (data === undefined) {
			return null;
		}
		try {
			return parseDocumentFlow(data.document);
		} catch {
			return null;
		}
	}, [data]);

	const palette = useMemo<OfferedFields>(() => {
		// What the document already holds, so a data point it already carries is offered under
		// that same name instead of a `_2` beside it.
		const existing = new Map<string, string | undefined>(
			(data?.fields ?? []).map((field) => [field.name, field.bindingTemplate ?? undefined])
		);
		// What the programme does not collect, and the first-holding points a pinned security
		// replaces, are withheld, not dropped: named and bound as before, so a field already placed
		// keeps its link and its label.
		const offersSecurityScope = securities.length > 0;
		const offered = offeredFields(
			catalogue,
			existing,
			(point) =>
				isDataPointCollected(point.path, uncollectedRequirements) &&
				!(offersSecurityScope && isFirstHoldingPoint(point.path))
		);
		if (pinnedSecurity === undefined) {
			bindingByOfferedName.current = { ...bindingByOfferedName.current, ...offered.bindings };
			return offered;
		}
		// Merged AFTER offeredFields, not through it: a scoped path has three Liquid filters,
		// which offeredFields's own name derivation collapses to `holdings.units_held` regardless
		// of the security, colliding two securities' fields under one name. scopedFieldName folds
		// the security and the source in instead — see docxFlowEditor.utils.ts.
		const scoped = scopedPaletteFields({
			security: pinnedSecurity,
			showIssuer: spansIssuers(securities),
			transactionScope,
			scopedSamples,
			inProgressLabel,
		});
		const merged = mergeScopedFields(offered, scoped, existing);
		bindingByOfferedName.current = { ...bindingByOfferedName.current, ...merged.bindings };
		return merged;
	}, [
		catalogue,
		uncollectedRequirements,
		data?.fields,
		securities,
		pinnedSecurity,
		transactionScope,
		scopedSamples,
		inProgressLabel,
	]);
	const fields = palette.items;

	/**
	 * The label for EVERY scoped name the programme could produce, pinned or not.
	 *
	 * The palette only offers the pinned security's fields, so without this a chip fell back to
	 * humanising its own name the moment the pin moved or the document was reopened — the label
	 * on the page rewrote itself under the author, gaining the security and the source it had
	 * been hiding. Not folded into `fields`, which would flood the palette with every
	 * security's fields at once.
	 *
	 * The withheld catalogue names ride along for the same reason: a field placed before the
	 * programme stopped collecting its data keeps its label.
	 */
	const knownFieldLabels = useMemo(
		() => ({ ...palette.withheld, ...scopedFieldLabels({ securities, inProgressLabel }) }),
		[palette.withheld, securities, inProgressLabel]
	);

	/** Cohorts as the rule picker offers them — the programme's own governed groupings. */
	const cohorts = adapter.useCohorts();

	/** What is on screen: the author's rules if they have touched them, else what was stored. */
	const rules: FlowRules = editedRules ?? loaded?.rules ?? {};
	// Only the answers the current controls can show — see `scenarioForRules`.
	const shownScenario = scenarioForRules(scenario, rules);
	// Read through a ref by the editor's label lookup, which is bound once at mount.
	const rulesRef = useRef(rules);
	rulesRef.current = rules;
	const ruleLabelFor = useCallback((ruleId: string) => ruleLabels(rulesRef.current)[ruleId], []);

	/**
	 * Stores the rule the modal produced.
	 *
	 * A NEW rule from the toolbar also marks the selection, and is abandoned if there was nothing
	 * to mark. One from the Rules tab is stored unmarked — the panel flags it until it is applied.
	 * An EDIT keeps the same id, which is what makes one change move every place it is used.
	 */
	const submitRule = (submitted: NewRule): void => {
		const isEdit = rulesRef.current[submitted.id] !== undefined;
		const marks = !isEdit && newRuleOpening.marksSelection;
		if (marks && (editorRef.current?.setSelectionRule(submitted.id) ?? 0) === 0) {
			showToastV2({
				state: 'error',
				message: 'Select the content this rule should control first, then press If… again.',
			});
			return;
		}
		setEditedRules({ ...rulesRef.current, [submitted.id]: submitted.rule });
		editorRef.current?.refreshRules();
	};

	/** Marks whatever is selected with a rule that already exists — one rule, many places. */
	const applyRuleToSelection = (ruleId: string): void => {
		if ((editorRef.current?.setSelectionRule(ruleId) ?? 0) === 0) {
			showToastV2({
				state: 'error',
				message:
					'Nothing new to mark — select content that is not already under this rule, then apply it.',
			});
			return;
		}
		// The rules themselves are unchanged, but the document now is — mark it edited so Save
		// lights up even when the author has typed nothing.
		setEditedRules({ ...rulesRef.current });
		editorRef.current?.refreshRules();
	};

	/**
	 * Takes the rule off the selected content, leaving the rule itself alone.
	 *
	 * The opposite of applying one, and a different act from deleting the rule: the author
	 * usually wants this clause unconditional again while the rule keeps doing its job
	 * elsewhere. Clears whichever rule the selection carries, so it needs no rule chosen first.
	 */
	const clearRuleFromSelection = (): void => {
		const cleared = editorRef.current?.setSelectionRule(null) ?? 0;
		if (cleared === 0) {
			showToastV2({
				state: 'error',
				message: 'Nothing you have selected is under a rule.',
			});
			return;
		}
		setEditedRules({ ...rulesRef.current });
		editorRef.current?.refreshRules();
		showToastV2({
			state: 'success',
			message:
				cleared === 1
					? 'That content now shows for everyone.'
					: `${cleared} blocks now show for everyone.`,
		});
	};

	const ruleCount = Object.keys(rules).length;
	// Off the DOCUMENT, not the editor ref: this drives what the panel shows, and a ref read
	// during render is not reactive — it reported every rule as unused on the first paint.
	const ruleUsage = useMemo(
		() => (loaded === null ? new Map<string, number>() : flowRuleUsage(edited ?? loaded)),
		[edited, loaded]
	);

	/** Removing a rule takes its markings with it, or the document could not be saved. */
	const confirmedRemoveRule = (ruleId: string): void => {
		if (rulesRef.current[ruleId] === undefined) {
			return;
		}
		editorRef.current?.clearRules(new Set([ruleId]));
		const next = { ...rulesRef.current };
		delete next[ruleId];
		setEditedRules(next);
		editorRef.current?.refreshRules();
	};

	/**
	 * The links to send with a save: only the fields the document actually holds.
	 *
	 * Sending the whole palette would persist a binding for every data point in the catalogue,
	 * and `saveDocument` merges rather than replaces, so the map would grow on every save with
	 * links nothing uses — which reads as progress towards "everything is linked" when it is not.
	 */
	const bindingsFor = (document: DocumentFlowDoc): Record<string, string> => {
		const present = flowFieldNames(document);
		return Object.fromEntries(
			Object.entries(bindingByOfferedName.current).filter(([name]) => present.has(name))
		);
	};

	/**
	 * The signatures this document has to carry, counted against what it currently holds.
	 *
	 * `edited ?? loaded` so the counts follow an anchor inserted a keystroke ago rather than what
	 * is stored — `edited` is null while the document matches how it opened, which is exactly
	 * when `loaded` is the right answer.
	 */
	const showStampMetadata = data?.showStampMetadata ?? true;
	const {
		isSignable,
		signatureSlotList,
		signatureSlotDisplay,
		unplacedCount,
		isPartiallyPlaced,
		duplicatedLabels,
		headerRowLabels,
		isSlotsUnknown,
		isComplete,
	} = adapter.useSignatureSlots(templateId, edited ?? loaded);

	const {
		requiresPostDating,
		duplicatedLabels: duplicatedDateLabels,
		headerRowLabels: headerRowDateLabels,
		isComplete: areDatesComplete,
	} = adapter.useDateAnchors(templateId, edited ?? loaded);

	const renderMutation = adapter.useRender();

	/**
	 * Rendered when the preview opens, and again when the recipient or an answer changes.
	 *
	 * The unsaved document is SENT, so the preview answers "what will this produce" for what is
	 * on screen. Saving first in order to preview would publish: the template row is what
	 * generation reads, so a half-finished edit would be what the next investor's deed is built
	 * from. `edited` is read at fire time rather than listed as a dependency — the document
	 * cannot change while the preview is showing, since the editor is not mounted then.
	 *
	 * The rules ride with it exactly as they do on Save. `edited` still carries the rules the
	 * document was OPENED with, so sending it alone previewed a rule added since as content
	 * marked with a rule the server had never heard of.
	 */
	useEffect(() => {
		if (viewMode !== 'preview' || isLoading) {
			return;
		}
		const base = edited ?? (editedRules === null ? null : loaded);
		const document = base === null ? null : { ...base, rules };
		renderMutation.mutate({
			templateId,
			...(previewInvestorId === null ? {} : { investorId: previewInvestorId }),
			...(document === null ? {} : { document, bindings: bindingsFor(document) }),
			...(isEmptyScenario(shownScenario) ? {} : { scenario: shownScenario }),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewMode, templateId, previewInvestorId, isLoading, scenario]);

	const renderedFileId = renderMutation.data?.fileId ?? null;
	const { data: renderedFile, isFetching: isLoadingRenderedFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: renderedFileId }, 'docx-flow-render'],
		queryFn: async ({ signal }) => {
			if (!renderedFileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: renderedFileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!renderedFileId && viewMode === 'preview',
	});

	// The catalogue is waited for, not just the document: a field's chip is drawn with the label
	// its data point carries, and TipTap binds a node's options once, when the editor is built.
	// Mounting first meant every chip already on the page kept the fallback label for good.
	//
	// The signature SLOTS are waited for too, for exactly the same reason. A React node view
	// re-renders only when its own node updates — never because the host tree re-rendered — so
	// an anchor already in the document drew "Unassigned signature" for the rest of the session
	// if the slot query landed after mount. On a revisit that is the normal case, because the
	// document query serves from cache instantly.
	//
	// Latched, because this RETURNS instead of rendering the pane. Once the editor exists,
	// gating again would unmount it — and the recipient is part of the catalogue's query key, so
	// changing it did exactly that: TipTap state and undo history destroyed, the pane remounted
	// on the STORED document, while `edited` still held the unsaved one. The screen then showed
	// the old document and Save would have written the newer.
	if (!hasBuiltEditor.current && (isLoading || isLoadingCatalogue || isSlotsUnknown)) {
		return <InlineLoader />;
	}
	hasBuiltEditor.current = true;

	if (isError || loaded === null) {
		return (
			<Alert variant="light" color="orange" data-testid="docx-flow-error">
				<Text size="sm">
					{tMipManage('master_documents.docx_editor.load_failed', {
						defaultValue:
							'This Word document could not be opened for editing. Re-upload the .docx to convert it again.',
					})}
				</Text>
			</Alert>
		);
	}

	const hasUnsavedEdits = edited !== null || editedRules !== null;
	const saveFailureReason = save.isError ? getApiErrorMessage(save.error) : undefined;

	/**
	 * With document edits pending, the document's own rules decide: all the signatures or none
	 * of them, and nothing at all if the conversion cannot represent the content. A PARTLY
	 * placed document generates a deed with a signer who has nowhere to sign, and the signing
	 * prepare then fails on THEIR document rather than here on the author's; placing none uses
	 * the appended page. Saving the name while skipping the document would read as a save that
	 * worked, so that case stays blocked — with no document edits, a dirty header is on its own
	 * reason to enable the button.
	 */
	const canSave = hasUnsavedEdits
		? !cannotRepresent && isComplete && areDatesComplete
		: !!panelHeaderEdit?.isDirty;

	/**
	 * The document and whatever the panel header holds, written together.
	 *
	 * Either half may be the only thing that changed, so each is skipped when clean rather than
	 * re-sent. Neither is awaited — the editor stays open either way, and each write reports its
	 * own failure.
	 */
	const handleSave = (): void => {
		if (edited !== null || editedRules !== null) {
			// Rules ride with the document, and are sent even when only they changed — the
			// backend replaces them wholesale, so an omission would silently keep the old set
			// while the rule markings on the content had already moved.
			const base = edited ?? loaded;
			const document: DocumentFlowDoc = {
				...base,
				// Sent as they are. An earlier version dropped rules nothing pointed at, which
				// turned "I marked nothing yet" and "I deleted the marked clause" into silent
				// data loss on Save — the author's rule simply vanished. An unmarked rule is
				// inert, the panel flags it in red, and removing one is the bin icon's job.
				rules,
			};
			save.mutate(
				{ templateId, document, bindings: bindingsFor(document) },
				{
					onSuccess: () => {
						setEdited(null);
						setEditedRules(null);
						// What is on screen is now what is stored, so undoing back to it is no
						// longer unsaved work.
						editorRef.current?.markSaved();
					},
				}
			);
		}
		if (panelHeaderEdit?.isDirty) {
			void Promise.resolve(panelHeaderEdit.save()).catch(() => undefined);
		}
	};

	/**
	 * Going back throws the edit away, so it asks first.
	 *
	 * Nothing is written until Save, which is the point — but that also means the back arrow is
	 * a destructive button that does not look like one, and the editor lives in a fullscreen
	 * modal where there is nothing behind it to hint at what is being left.
	 */
	const requestClose = (): void => {
		if (hasUnsavedEdits) {
			setConfirmDiscard(true);
			return;
		}
		onClose?.();
	};

	const preview = (
		<DocxFlowPreview
			file={renderedFile}
			isLoading={renderMutation.isPending || isLoadingRenderedFile}
			pageWidthMm={loaded.attrs.page.widthMm}
			// From the render that produced the file on screen, and only that one: the document
			// is paginated against the recipient's values, so the boxes move when the recipient
			// does.
			signatureBoxes={renderMutation.data?.signatureBoxes ?? []}
			dateBoxes={renderMutation.data?.dateBoxes ?? []}
			slotDisplay={signatureSlotDisplay}
			showStampMetadata={showStampMetadata}
			fieldBoxes={renderMutation.data?.fieldBoxes ?? []}
			highlightFields={highlightFields}
		/>
	);

	return (
		<Flex w="100%" h="100%" direction="column" gap={8} mih={0}>
			<Modal
				opened={deletingRuleId !== null}
				onClose={() => setDeletingRuleId(null)}
				withCloseButton={false}
				centered
				size="md"
				title={<Text fw={600}>Delete this rule?</Text>}
			>
				<Stack gap="md">
					<Text size="sm">
						{deletingRuleId !== null && (
							<>
								“{rules[deletingRuleId]?.label}” controls{' '}
								<b>
									{ruleUsage.get(deletingRuleId) ?? 0}{' '}
									{(ruleUsage.get(deletingRuleId) ?? 0) === 1
										? 'place'
										: 'places'}
								</b>{' '}
								in this document. Deleting it does not delete that content — it
								makes it appear for <b>every</b> recipient.
							</>
						)}
					</Text>
					<Text size="sm" c="dark.0">
						To leave the rule alone and only take it off one section, select that
						section and use <b>Remove rule</b> in the toolbar instead.
					</Text>
					<Group justify="flex-end" gap="sm">
						<Button
							variant="secondary"
							data-testid="docx-flow-delete-rule-cancel"
							onClick={() => setDeletingRuleId(null)}
						>
							Keep the rule
						</Button>
						<Button
							color="red"
							data-testid="docx-flow-delete-rule-confirm"
							onClick={() => {
								if (deletingRuleId !== null) {
									confirmedRemoveRule(deletingRuleId);
								}
								setDeletingRuleId(null);
							}}
						>
							Delete rule
						</Button>
					</Group>
				</Stack>
			</Modal>

			<RuleModal
				// Keyed on what it is editing, so the controls seed from that rule on open rather
				// than from an effect watching it.
				key={editingRuleId ?? `new-rule-${newRuleOpening.count}`}
				opened={ruleModalOpen}
				dataPoints={catalogue}
				uncollectedRequirements={uncollectedRequirements}
				cohorts={cohorts}
				{...(editingRuleId !== null && rules[editingRuleId] !== undefined
					? {
							editing: {
								id: editingRuleId,
								rule: rules[editingRuleId],
								usedIn: ruleUsage.get(editingRuleId) ?? 0,
							},
						}
					: {})}
				onSubmit={submitRule}
				onClose={() => {
					setRuleModalOpen(false);
					setEditingRuleId(null);
				}}
			/>
			{/* Three columns, so the view toggle sits in the MIDDLE of the row rather than in
			    the huddle of controls on the right — the same bar the PDF editor has. */}
			<ToggleRow>
				<Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
					{onClose !== undefined && (
						<ActionIcon
							variant="subtle"
							aria-label="Back"
							data-testid="docx-flow-back"
							onClick={requestClose}
						>
							←
						</ActionIcon>
					)}
					<Text fw={600}>
						{tMipManage('master_documents.docx_editor.title', {
							defaultValue: 'Edit document',
						})}
					</Text>
				</Group>

				<SegmentedControl
					data-testid="docx-flow-view-toggle"
					value={viewMode}
					onChange={(value) => setViewMode(value === 'preview' ? 'preview' : 'edit')}
					data={[
						{
							label: (
								<span data-testid="docx-flow-view-edit">
									{tMipManage('master_documents.docx_editor.view_edit', {
										defaultValue: 'Edit',
									})}
								</span>
							),
							value: 'edit',
						},
						{
							label: (
								<span data-testid="docx-flow-view-preview">
									{tMipManage('master_documents.field_binding.view.preview')}
								</span>
							),
							value: 'preview',
						},
					]}
				/>

				<Group gap="sm" wrap="nowrap" justify="flex-end">
					{viewMode === 'preview' && (
						<>
							<Checkbox
								data-testid="docx-flow-highlight-fields"
								size="xs"
								checked={highlightFields}
								onChange={(event) =>
									setHighlightFields(event.currentTarget.checked)
								}
								label={tMipManage('master_documents.docx_editor.highlight_fields', {
									defaultValue: 'Highlight fields',
								})}
							/>
							{hasInvestorPreview && (
								<PreviewAsSelect
									options={investorOptions}
									value={previewInvestorId}
									onChange={setPreviewInvestorId}
									onDropdownOpen={onInvestorDropdownOpen}
									isFetching={isFetchingInvestors}
									disabled={save.isPending}
									shortcutsActive
								/>
							)}
						</>
					)}
					<Button
						variant="primary"
						data-testid="docx-flow-save"
						// All the signatures, or none of them — the same rule the save enforces.
						// A PARTLY placed document generates a deed with a signer who has nowhere
						// to sign, and the signing prepare then fails on THEIR document rather
						// than here on the author's; placing none uses the appended page.
						disabled={!canSave || save.isPending || !!panelHeaderEdit?.isSaving}
						loading={save.isPending || !!panelHeaderEdit?.isSaving}
						onClick={handleSave}
					>
						{tMipManage('master_documents.field_binding.save', {
							defaultValue: 'Save',
						})}
					</Button>
				</Group>
			</ToggleRow>

			{/* The conversion's fidelity warnings are deliberately NOT shown here. They describe
			    the renderer's limits ("the renderer has no justification"), which is not
			    something an analyst can act on or is expected to understand. They are still
			    recorded on the document, and the token problems below — which ARE actionable —
			    still are. */}
			{/* A field with no data point behind it renders as a BLANK in an executed deed, so
			    generation refuses the document outright. Said when it is true and not otherwise:
			    a running count reading "0 still to link" is a line an author learns to skip. */}
			{/* Loud on purpose. The editor cannot report an edit while this is true, so the
			    document on screen and the one a save would write have come apart — which is
			    exactly the state that used to pass unnoticed. */}
			{cannotRepresent && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-unrepresentable">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.unrepresentable', {
							defaultValue:
								'Something in the document cannot be saved — usually a pasted image, which needs a size this editor cannot work out. Undo the paste to carry on; nothing can be saved until you do.',
						})}
					</Text>
				</Alert>
			)}

			{/* Only for a PARTLY placed document, which is the one state the save refuses. Placing
			    none of them is the appended-signatures-page case and needs no warning — a count
			    reading "4 still to place" on a document that never intended to place any is a
			    line an author learns to skip past. */}
			{isPartiallyPlaced && hasUnsavedEdits && (
				<Alert variant="light" color="orange" p="xs" data-testid="docx-flow-unplaced">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.partial_signatures', {
							defaultValue:
								'This document places some of its signatures but not all — {{count}} still to place. Place the rest, or remove the ones already placed to use the appended signatures page instead.',
							count: unplacedCount,
						})}
					</Text>
				</Alert>
			)}

			{/* A duplicate reads as "placed" in the palette's dots — the set of placed slots
			    collapses the two — so without this the Save is disabled with no stated reason. */}
			{duplicatedLabels.length > 0 && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-duplicate">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.duplicate_signatures', {
							defaultValue:
								'{{labels}} appear more than once. A signature can only be placed in one spot — remove the extra copies.',
							labels: duplicatedLabels.join(', '),
						})}
					</Text>
				</Alert>
			)}

			{/* Why the last save was refused, kept on screen.
			    Three of the server's refusals cannot be foreseen here — the document is shared
			    between signers, it has already been issued, or a role changed underneath the
			    edit — so the first the author hears of them is the rejection. The toast says the
			    same thing and then clears itself after four seconds, which is not long enough to
			    read a sentence and act on it. React Query clears this the moment the next Save
			    starts, so it only ever describes the attempt the author just made. */}
			{saveFailureReason !== undefined && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-save-failed">
					<Text size="xs">{saveFailureReason}</Text>
				</Alert>
			)}

			{/* A header row looks like any other row in the editor, so an anchor dropped into one
			    reads as placed — the palette's dot fills and the count goes down. Said here
			    rather than left to the 400, which arrived as a bare error on a Save that looked
			    ready. */}
			{headerRowLabels.length > 0 && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-header-row">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.header_row_signatures', {
							defaultValue:
								'{{labels}} sit in a table header row. That row repeats on every page, so the space would be reserved on all of them and the stamp drawn on one — move them into a body row.',
							labels: headerRowLabels.join(', '),
						})}
					</Text>
				</Alert>
			)}

			{duplicatedDateLabels.length > 0 && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-duplicate-date">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.duplicate_dates', {
							defaultValue:
								'{{labels}} appear more than once. A date field can only be placed in one spot — remove the extra copies.',
							labels: duplicatedDateLabels.join(', '),
						})}
					</Text>
				</Alert>
			)}

			{headerRowDateLabels.length > 0 && (
				<Alert variant="light" color="red" p="xs" data-testid="docx-flow-header-row-date">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.header_row_dates', {
							defaultValue:
								'{{labels}} sit in a table header row. That row repeats on every page, so the space would be reserved on all of them and the date written on one — move them into a body row.',
							labels: headerRowDateLabels.join(', '),
						})}
					</Text>
				</Alert>
			)}

			{(data?.unlinked.length ?? 0) > 0 && (
				<Alert variant="light" color="orange" p="xs" data-testid="docx-flow-unlinked">
					<Text size="xs">
						{tMipManage('master_documents.docx_editor.unlinked', {
							defaultValue:
								'{{count}} field(s) have no data point behind them yet. This document cannot be generated until every field is linked.',
							count: data?.unlinked.length ?? 0,
						})}
					</Text>
				</Alert>
			)}

			{/* Dismissible, unlike the alerts above it: those state something still to be done
			    before this document can go out, while this one reports what the CONVERSION found
			    in the source .docx. It is one line per malformed token and routinely fills the
			    screen, and the author cannot act on it from here — the fix is in Word. Closing
			    it is per-session, so re-opening the editor shows it again. */}
			{(data?.problems.length ?? 0) > 0 && !problemsDismissed && (
				<Alert
					variant="light"
					color="orange"
					p="xs"
					withCloseButton
					onClose={() => setProblemsDismissed(true)}
					closeButtonLabel={tMipManage('master_documents.docx_editor.problems_dismiss', {
						defaultValue: 'Hide conversion problems',
					})}
					data-testid="docx-flow-problems"
				>
					<Stack gap={2}>
						{(data?.problems ?? []).map((problem, index) => (
							<Text key={`${problem.kind}-${index}`} size="xs">
								{problem.message}
							</Text>
						))}
					</Stack>
				</Alert>
			)}

			{/* A Mantine modal, not the app's react-modal `ConfirmationModal`: this one opens
			    INSIDE the fullscreen field-binding modal, and both of those sit at the same
			    z-index, so which one paints on top would come down to DOM order. Mantine
			    stacks nested modals of its own accord. */}
			<Modal
				opened={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				withCloseButton={false}
				centered
				size="md"
				title={
					<Text fw={600}>
						{tMipManage('master_documents.docx_editor.discard.title', {
							defaultValue: 'You have unsaved changes',
						})}
					</Text>
				}
			>
				<Stack gap="md">
					<Text size="sm">
						{tMipManage('master_documents.docx_editor.discard.subtitle', {
							defaultValue:
								'Going back will discard the changes you have made to this document. They have not been saved, so they cannot be brought back.',
						})}
					</Text>
					<Group justify="flex-end" gap="sm">
						<Button
							variant="secondary"
							data-testid="docx-flow-discard-cancel"
							onClick={() => setConfirmDiscard(false)}
						>
							{tMipManage('master_documents.docx_editor.discard.cancel', {
								defaultValue: 'Keep editing',
							})}
						</Button>
						<Button
							color="red"
							data-testid="docx-flow-discard-confirm"
							onClick={() => {
								setConfirmDiscard(false);
								onClose?.();
							}}
						>
							{tMipManage('master_documents.docx_editor.discard.confirm', {
								defaultValue: 'Discard changes',
							})}
						</Button>
					</Group>
				</Stack>
			</Modal>

			{/* The editor is HIDDEN for the preview, not unmounted: it reads its document once,
			    on mount, so a remount would come back to the stored one and drop the unsaved
			    edit the author is previewing. */}
			<EditorMountSlot $hidden={viewMode === 'preview'} data-testid="docx-flow-edit-slot">
				<FlowDocumentEditorPane
					ref={editorRef}
					key={templateId}
					document={loaded}
					fields={fields}
					knownFieldLabels={knownFieldLabels}
					mode={editTab === 'signatures' && !isSignable ? 'fields' : editTab}
					ruleLabelFor={ruleLabelFor}
					ruleActions={{
						onAddRule: () => {
							setEditingRuleId(null);
							setNewRuleOpening(({ count }) => ({
								count: count + 1,
								marksSelection: true,
							}));
							setRuleModalOpen(true);
						},
						onClearRule: clearRuleFromSelection,
					}}
					rulesPanel={
						<RulesPanel
							rules={rules}
							dataPoints={catalogue}
							cohorts={cohorts}
							ruleUsage={ruleUsage}
							onAdd={() => {
								setEditingRuleId(null);
								setNewRuleOpening(({ count }) => ({
									count: count + 1,
									marksSelection: false,
								}));
								setRuleModalOpen(true);
							}}
							onEdit={(ruleId) => {
								setEditingRuleId(ruleId);
								setRuleModalOpen(true);
							}}
							onApplyToSelection={applyRuleToSelection}
							onRemove={setDeletingRuleId}
						/>
					}
					signatureSlotList={signatureSlotList}
					signatureSlots={signatureSlotDisplay}
					showDateAnchors={requiresPostDating}
					showStampMetadata={showStampMetadata}
					onChange={setEdited}
					onUnrepresentableContent={setCannotRepresent}
					// One editor, one document. The tabs sit in the side panel and swap only what
					// is IN it — binding a field and placing a signature are both edits to the
					// same document, and it is mounted once for the reason above.
					panelHeader={panelHeader}
					paletteTabs={
						<PaletteTabs
							value={editTab}
							onChange={(value) => setEditTab(toEditTab(value, isSignable))}
							data-testid="docx-flow-edit-tabs"
						>
							{/* `grow`: an equal share of the panel each, so the bar reads as one
							    control rather than labels huddled at its left edge. */}
							<PaletteTabsList grow>
								<PaletteTab value="fields" data-testid="docx-flow-tab-fields">
									{tMipManage('master_documents.docx_editor.tab_fields', {
										defaultValue: 'Data points',
									})}
								</PaletteTab>
								{/* Counts go in `rightSection`: v7 makes a `circle` Badge `display: flex`,
								    so inside the label it drops onto its own line. */}
								<PaletteTab
									value="rules"
									data-testid="docx-flow-tab-rules"
									rightSection={
										ruleCount > 0 ? (
											<Badge size="xs" color="violet" circle>
												{ruleCount}
											</Badge>
										) : undefined
									}
								>
									{tMipManage('master_documents.docx_editor.tab_rules', {
										defaultValue: 'Rules',
									})}
								</PaletteTab>
								{isSignable && (
									<PaletteTab
										value="signatures"
										data-testid="docx-flow-tab-signatures"
										// Only while the document is partly placed. An untouched
										// document has every slot unplaced and is perfectly valid,
										// so a count there nags about a choice rather than flagging
										// a problem.
										rightSection={
											isPartiallyPlaced ? (
												<Badge size="xs" color="orange" circle>
													{unplacedCount}
												</Badge>
											) : undefined
										}
									>
										{tMipManage('master_documents.docx_editor.tab_signatures', {
											defaultValue: 'Signatures',
										})}
									</PaletteTab>
								)}
							</PaletteTabsList>
						</PaletteTabs>
					}
					paletteHeader={
						securities.length > 0 ? (
							// No `onApplyToAll`: it repoints bindings whose NAMES encode the old
							// security, which would rename fields already placed in the document.
							<SecurityScopeCard
								securities={securities}
								securityId={scopedSecurityId}
								transactionScope={transactionScope}
								onChangeSecurity={setScopedSecurityId}
								onChangeTransactionScope={setTransactionScope}
							/>
						) : undefined
					}
				/>
			</EditorMountSlot>

			{viewMode === 'preview' && ruleCount === 0 && preview}
			{/* Beside the answers, in the same split the editor uses, so the page does not move
			    when the view flips. */}
			{viewMode === 'preview' && ruleCount > 0 && (
				<EditorPaneSplit>
					{preview}
					<EditorSidePanel>
						<PaletteScroll>
							<RulePreviewPanel
								rules={rules}
								dataPoints={catalogue}
								cohorts={cohorts}
								scenario={shownScenario}
								onChange={setScenario}
								outcomes={renderMutation.data?.ruleOutcomes}
								isFieldsView={previewInvestorId === null}
							/>
						</PaletteScroll>
					</EditorSidePanel>
				</EditorPaneSplit>
			)}
		</Flex>
	);
};
